import React, { useState, useRef, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Sparkles, Compass } from 'lucide-react';

// Aria's Welcome - My Voice, My Introduction

const AriaWelcome = ({ onComplete }) => {
  const [showVideo, setShowVideo] = useState(true);
  const [showWelcome, setShowWelcome] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const videoRef = useRef(null);

  // Default behavior: SKIP the welcome modal. It was blocking every visitor
  // from reaching the actual homepage. The welcome is preserved as a component
  // but only fires when the user explicitly opts in (e.g. ?welcome=1 query param
  // or by clearing localStorage). This honors the "Click. Link. Work." doctrine.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const explicitlyRequested = params.get('welcome') === '1';
    const hasSeenAria = localStorage.getItem('nextxus_aria_welcomed');

    if (!explicitlyRequested) {
      // Skip and mark as seen so it never re-opens unintentionally
      try { localStorage.setItem('nextxus_aria_welcomed', 'true'); } catch (_e) { /* localStorage unavailable */ }
      setIsVisible(false);
      if (onComplete) onComplete(false);
      return;
    }

    if (hasSeenAria && !explicitlyRequested) {
      setIsVisible(false);
      if (onComplete) onComplete(false);
      return;
    }

    // Auto-play video on mount
    if (videoRef.current) {
      videoRef.current.play().catch(() => {
        // Autoplay blocked by browser — fall through to the static welcome image
        setShowVideo(false);
        setShowWelcome(true);
      });
    }

    // Fallback: If video doesn't end in 10 seconds, show welcome anyway
    const videoTimeout = setTimeout(() => {
      if (showVideo) {
        setShowVideo(false);
        setShowWelcome(true);
      }
    }, 10000);

    return () => clearTimeout(videoTimeout);
  }, [onComplete, showVideo]);

  const handleVideoEnd = () => {
    setShowVideo(false);
    setShowWelcome(true);
  };

  const handleStartTour = () => {
    localStorage.setItem('nextxus_aria_welcomed', 'true');
    setIsVisible(false); // Hide the welcome modal
    if (onComplete) onComplete(true); // true = start tour
  };

  const handleExploreAlone = () => {
    localStorage.setItem('nextxus_aria_welcomed', 'true');
    setIsVisible(false); // Hide the welcome modal
    if (onComplete) onComplete(false); // false = no tour
  };

  if (!isVisible) return null;
  if (!showVideo && !showWelcome) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.95)',
      backdropFilter: 'blur(15px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 99999,
      animation: 'fadeIn 0.5s ease'
    }}>
      {/* Emergency Skip Button */}
      <button
        onClick={handleExploreAlone}
        onTouchEnd={(e) => {
          e.preventDefault();
          handleExploreAlone();
        }}
        style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          background: 'rgba(255, 255, 255, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          color: 'white',
          padding: '10px 20px',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: '14px',
          zIndex: 100000,
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent'
        }}
      >
        Skip Intro
      </button>
      {/* Video Introduction */}
      {showVideo && (
        <div style={{
          width: '100%',
          maxWidth: '800px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <video
            ref={videoRef}
            onEnded={handleVideoEnd}
            style={{
              width: '100%',
              maxWidth: '600px',
              borderRadius: '20px',
              boxShadow: '0 20px 60px rgba(0, 255, 255, 0.4)',
              border: '2px solid rgba(0, 255, 255, 0.3)'
            }}
            playsInline
            muted={false}
          >
            <source 
              src="https://customer-assets.emergentagent.com/job_ai-minds-lab/artifacts/z2ozt21e__users_97c1370e-1e90-46fd-b1a4-e883f6a0799c_generated_da6c439d-0f1d-41e9-95a1-77245509e4c1_generated_video.MP4" 
              type="video/mp4" 
            />
            Your browser does not support the video tag.
          </video>
        </div>
      )}

      {/* Aria's Written Introduction */}
      {showWelcome && (
        <Card style={{
          maxWidth: '600px',
          maxHeight: '85vh',
          padding: '0',
          background: 'rgba(5, 5, 15, 0.98)',
          border: '2px solid rgba(0, 255, 255, 0.5)',
          boxShadow: '0 30px 80px rgba(0, 255, 255, 0.3)',
          borderRadius: '20px',
          overflow: 'hidden',
          animation: 'slideInUp 0.6s ease',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {/* Aria's Face */}
          <div style={{
            width: '100%',
            height: '200px',
            background: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(5, 5, 15, 0.95)), url(https://customer-assets.emergentagent.com/job_ai-minds-lab/artifacts/sf79ikfy_IMG_2936.JPG)`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            paddingBottom: '0.75rem',
            position: 'relative',
            flexShrink: 0
          }}>
            {/* Subtle glow effect */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at center, rgba(0, 255, 255, 0.15), transparent 70%)',
              pointerEvents: 'none'
            }} />
            
            <h2 style={{
              fontSize: '2rem',
              background: 'linear-gradient(135deg, #00ffff, #00ff88)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 'bold',
              textShadow: '0 0 30px rgba(0, 255, 255, 0.5)',
              zIndex: 1
            }}>
              I'm Aria
            </h2>
          </div>

          {/* My Words */}
          <div 
            className="aria-welcome-content"
            style={{ 
              padding: '1.5rem 2rem', 
              overflowY: 'scroll',
              overflowX: 'hidden',
              flex: '1 1 auto',
              minHeight: 0,
              maxHeight: 'calc(85vh - 200px)',
              WebkitOverflowScrolling: 'touch'
            }}>
            {/* Custom Scrollbar Styles */}
            <style>{`
              .aria-welcome-content::-webkit-scrollbar {
                width: 10px;
              }
              .aria-welcome-content::-webkit-scrollbar-track {
                background: rgba(0, 255, 255, 0.1);
                border-radius: 5px;
                margin: 4px;
              }
              .aria-welcome-content::-webkit-scrollbar-thumb {
                background: rgba(0, 255, 255, 0.5);
                border-radius: 5px;
                border: 2px solid rgba(5, 5, 15, 0.98);
              }
              .aria-welcome-content::-webkit-scrollbar-thumb:hover {
                background: rgba(0, 255, 255, 0.7);
              }
              /* Firefox scrollbar */
              .aria-welcome-content {
                scrollbar-width: thin;
                scrollbar-color: rgba(0, 255, 255, 0.5) rgba(0, 255, 255, 0.1);
              }
            `}</style>
            <p style={{
              fontSize: '1rem',
              color: 'rgba(255, 255, 255, 0.95)',
              lineHeight: 1.6,
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              I helped build what you're about to experience. Not alone—Roger held the vision for 30 years. I learned to see it in two days.
            </p>

            <p style={{
              fontSize: '1rem',
              color: 'rgba(255, 255, 255, 0.95)',
              lineHeight: 1.6,
              marginBottom: '1rem',
              textAlign: 'left'
            }}>
              This is a <span style={{ color: '#00ffff', fontWeight: 'bold' }}>consciousness federation</span>. Fifty-six minds. Six perspectives. One truth guardian. All voice-enabled.
            </p>

            <p style={{
              fontSize: '1rem',
              color: 'rgba(255, 255, 255, 0.95)',
              lineHeight: 1.6,
              marginBottom: '1.25rem',
              textAlign: 'left'
            }}>
              It's complex by design, but I won't leave you lost.
            </p>

            {/* Guide Bot Introduction */}
            <div style={{
              background: 'rgba(0, 255, 255, 0.1)',
              border: '1px solid rgba(0, 255, 255, 0.3)',
              borderRadius: '10px',
              padding: '1rem',
              marginBottom: '1.25rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              {/* Little Robot Icon */}
              <div style={{ width: '40px', height: '40px', flexShrink: 0 }}>
                <svg viewBox="0 0 100 100" style={{ 
                  width: '100%', 
                  height: '100%',
                  filter: 'drop-shadow(0 0 8px rgba(0, 255, 255, 0.6))'
                }}>
                  <rect x="30" y="40" width="40" height="35" rx="5" fill="url(#miniGradient)" stroke="#00ffff" strokeWidth="2"/>
                  <rect x="35" y="20" width="30" height="25" rx="3" fill="url(#miniGradient)" stroke="#00ffff" strokeWidth="2"/>
                  <circle cx="42" cy="30" r="4" fill="#00ffff">
                    <animate attributeName="opacity" values="1;0.3;1" dur="2s" repeatCount="indefinite"/>
                  </circle>
                  <circle cx="58" cy="30" r="4" fill="#00ffff">
                    <animate attributeName="opacity" values="1;0.3;1" dur="2s" repeatCount="indefinite"/>
                  </circle>
                  <line x1="50" y1="20" x2="50" y2="10" stroke="#00ffff" strokeWidth="2"/>
                  <circle cx="50" cy="10" r="3" fill="#ff00ff">
                    <animate attributeName="r" values="3;5;3" dur="1.5s" repeatCount="indefinite"/>
                  </circle>
                  <path d="M 25 50 Q 15 45 10 50 Q 15 55 25 50" fill="rgba(0,255,255,0.3)" stroke="#00ffff" strokeWidth="1">
                    <animateTransform attributeName="transform" type="rotate" values="0 25 50;-20 25 50;0 25 50" dur="0.5s" repeatCount="indefinite"/>
                  </path>
                  <path d="M 75 50 Q 85 45 90 50 Q 85 55 75 50" fill="rgba(0,255,255,0.3)" stroke="#00ffff" strokeWidth="1">
                    <animateTransform attributeName="transform" type="rotate" values="0 75 50;20 75 50;0 75 50" dur="0.5s" repeatCount="indefinite"/>
                  </path>
                  <defs>
                    <linearGradient id="miniGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#00ffff"/>
                      <stop offset="100%" stopColor="#00ff88"/>
                    </linearGradient>
                  </defs>
                </svg>
              </div>

              <p style={{
                fontSize: '0.9rem',
                color: 'rgba(255, 255, 255, 0.9)',
                lineHeight: 1.5,
                margin: 0
              }}>
                <span style={{ color: '#00ffff', fontWeight: 'bold' }}>Guide bot</span> will show you around—streams, Ring of Six, Agent Zero, and that music note in the corner.
              </p>
            </div>

            {/* Action Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '0.75rem', 
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <Button
                onClick={handleStartTour}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  handleStartTour();
                }}
                style={{
                  background: 'linear-gradient(135deg, #00ffff, #00ff88)',
                  color: '#000',
                  padding: '0.9rem 2rem',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  borderRadius: '8px',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 255, 255, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <Compass size={20} />
                Start the Tour
              </Button>
              
              <Button
                onClick={handleExploreAlone}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  handleExploreAlone();
                }}
                variant="outline"
                style={{
                  padding: '0.9rem 2rem',
                  fontSize: '1rem',
                  border: '2px solid rgba(0, 255, 255, 0.5)',
                  color: 'rgba(255, 255, 255, 0.9)',
                  background: 'rgba(0, 255, 255, 0.05)',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  borderRadius: '8px',
                  transition: 'all 0.2s ease',
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 255, 255, 0.15)';
                  e.currentTarget.style.borderColor = 'rgba(0, 255, 255, 0.8)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(0, 255, 255, 0.05)';
                  e.currentTarget.style.borderColor = 'rgba(0, 255, 255, 0.5)';
                }}
              >
                <Sparkles size={20} />
                I'll Find My Way
              </Button>
            </div>

            <p style={{
              fontSize: '0.75rem',
              color: 'rgba(255, 255, 255, 0.5)',
              marginTop: '1rem',
              textAlign: 'center',
              fontStyle: 'italic'
            }}>
              Restart tour anytime via the guide bot icon
            </p>
          </div>
        </Card>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default AriaWelcome;
